import React, { useState } from 'react';
import './VendorManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const amount = (value) => (value === null || value === undefined ? '-' : `${Number(value).toFixed(2)} EUR`);

const median = (values) => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const EngineOpistoSync = () => {
  const [engineCode, setEngineCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);

  const handleSync = async () => {
    const normalizedCode = engineCode.trim().toUpperCase();
    if (!normalizedCode) {
      setError('Renseignez un code moteur.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let page = 0;
      let rawHitCount = 0;
      let pagesRead = 0;
      let listings = [];
      let errors = [];

      while (true) {
        const response = await fetch(`${API_BASE_URL}/engine-opisto/sync`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ engineCode: normalizedCode, page }),
        });
        const data = await response.json();
        if (!response.ok || data.success === false) {
          throw new Error(data.error || 'La synchronisation Opisto a échoué.');
        }

        rawHitCount = data.rawHitCount || rawHitCount;
        pagesRead += data.pagesRead || 1;
        listings = listings.concat(data.listings || []);
        errors = errors.concat(data.errors || []);

        if (data.engineCompleted) {
          const deliveredPrices = listings
            .filter((listing) => listing.totalDeliveredFrance !== null && listing.totalDeliveredFrance !== undefined)
            .map((listing) => Number(listing.totalDeliveredFrance))
            .filter((price) => Number.isFinite(price));
          const extrapolatedPrices = listings
            .filter((listing) => listing.ancienEquivalentHt !== null && listing.ancienEquivalentHt !== undefined)
            .map((listing) => Number(listing.ancienEquivalentHt))
            .filter((price) => Number.isFinite(price));
          setResult({
            ...data,
            rawHitCount,
            pagesRead,
            count: listings.length,
            missingMileageCount: listings.filter((listing) => listing.kilometers === null).length,
            averageTotalDeliveredFrance: deliveredPrices.length === 0
              ? null
              : Math.round((deliveredPrices.reduce((sum, price) => sum + price, 0) / deliveredPrices.length) * 100) / 100,
            ancienPrix: median(extrapolatedPrices),
            listings,
            errors,
          });
          break;
        }

        page = (data.page || page) + 1;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    let offset = 0;
    let enginePage = 0;

    try {
      while (true) {
        const response = await fetch(`${API_BASE_URL}/engine-opisto/sync-batch`, {
          method: 'POST', headers: getAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify({ offset, enginePage }),
        });
        const data = await response.json();
        if (!response.ok || data.success === false) throw new Error(data.error || 'La synchronisation de la liste a échoué.');
        setBatchProgress({ processed: offset + data.processed, total: data.total, code: data.engineCode, ancienPrix: data.ancienPrix });
        if (data.completed) break;
        offset = data.nextOffset;
        enginePage = data.nextEnginePage || 0;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vendor-management">
      <h2>Synchro Opisto</h2>
      <p>Recherche les annonces moteur, le prix livré France et le kilométrage affiché sur chaque fiche.</p>
      {error && <div className="error-message">{error}</div>}
      <div className="search-bar">
        <input
          type="text"
          value={engineCode}
          onChange={(event) => setEngineCode(event.target.value)}
          placeholder="Code moteur, par exemple WLAA"
          disabled={loading}
          onKeyDown={(event) => event.key === 'Enter' && handleSync()}
        />
        <button className="btn btn-primary" onClick={handleSync} disabled={loading || !engineCode.trim()}>
          {loading ? 'Synchro Opisto...' : 'Synchroniser'}
        </button>
        <button className="btn btn-secondary" onClick={handleSyncAll} disabled={loading}>
          Synchroniser tous les codes BDD
        </button>
      </div>

      {batchProgress && <p>Liste BDD : {batchProgress.processed} / {batchProgress.total} — dernier code : {batchProgress.code || '-'}{batchProgress.ancienPrix ? ` — ancien prix : ${amount(batchProgress.ancienPrix)}` : ''}</p>}

      {result && (
        <>
          <div className="search-section" style={{ textAlign: 'left' }}>
            <h3>Résultat pour {result.engineCode}</h3>
            <p>{result.count} moteur(s) en vente — moyenne livrée France : {amount(result.averageTotalDeliveredFrance)}</p>
            <p>Prix Opisto extrapolé à 100&nbsp;001 km : {amount(result.ancienPrix)}</p>
            {result.missingMileageCount > 0 && <p>{result.missingMileageCount} fiche(s) sans kilométrage renseigné.</p>}
            {result.errors?.length > 0 && <p>{result.errors.length} fiche(s) n’ont pas pu être lues. Dernière erreur : {result.errors[result.errors.length - 1]}</p>}
          </div>
          <div className="vendors-list">
            {result.listings.length === 0 ? <p className="no-data">Aucune annonce moteur exacte trouvée.</p> : (
              <table className="vendors-table">
                <thead><tr><th>Véhicule</th><th>Code Opisto</th><th>KM</th><th>Prix</th><th>FDP</th><th>Prix livré</th><th>Année</th><th>Fiche</th></tr></thead>
                <tbody>{result.listings.map((listing) => (
                  <tr key={listing.id}>
                    <td>{listing.vehicle || '-'}</td><td>{listing.opistoEngineCode || '-'}</td>
                    <td>{listing.kilometers === null ? '-' : `${listing.kilometers.toLocaleString('fr-FR')} km`}</td>
                    <td>{amount(listing.price)}</td><td>{amount(listing.shippingFrance)}</td><td>{amount(listing.totalDeliveredFrance)}</td>
                    <td>{listing.year || '-'}</td><td><a href={listing.url} target="_blank" rel="noopener noreferrer">Ouvrir</a></td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EngineOpistoSync;
